/* import __COLOCATED_TEMPLATE__ from './standing-out.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type { CxBenchmarkGroup, CxBenchmarkReasons } from './types';

type Sentiment = 'positive' | 'negative';

interface StandingOutItem {
  sentence: string;
  tooltip: string;
  sentiment: Sentiment;
  dotColorClass: string;
}

const DOT_COLOR_MAP: Record<Sentiment, string> = {
  positive: 'bg-success-fill',
  negative: 'bg-error-fill',
};

const DIFF_THRESHOLD = 0.05;

type ReasonKey = keyof CxBenchmarkReasons;

interface ReasonConfig {
  sentenceAboveKey: string;
  sentenceBelowKey: string;
  tooltipKey: string;
  isDesirable: boolean;
  oppositeKey: ReasonKey;
}

const REASON_CONFIG: Record<ReasonKey, ReasonConfig> = {
  answer_quality_bot_high: {
    sentenceAboveKey: 'sentence-high-fin-quality-above',
    sentenceBelowKey: 'sentence-high-fin-quality-below',
    tooltipKey: 'tooltip-high-fin-quality',
    isDesirable: true,
    oppositeKey: 'answer_quality_bot_low',
  },
  answer_quality_bot_low: {
    sentenceAboveKey: 'sentence-low-fin-quality-above',
    sentenceBelowKey: 'sentence-low-fin-quality-below',
    tooltipKey: 'tooltip-low-fin-quality',
    isDesirable: false,
    oppositeKey: 'answer_quality_bot_high',
  },
  product_feedback_positive: {
    sentenceAboveKey: 'sentence-positive-product-feedback-above',
    sentenceBelowKey: 'sentence-positive-product-feedback-below',
    tooltipKey: 'tooltip-positive-product-feedback',
    isDesirable: true,
    oppositeKey: 'product_feedback_negative',
  },
  product_feedback_negative: {
    sentenceAboveKey: 'sentence-negative-product-feedback-above',
    sentenceBelowKey: 'sentence-negative-product-feedback-below',
    tooltipKey: 'tooltip-negative-product-feedback',
    isDesirable: false,
    oppositeKey: 'product_feedback_positive',
  },
  policy_feedback_positive: {
    sentenceAboveKey: 'sentence-positive-policy-feedback-above',
    sentenceBelowKey: 'sentence-positive-policy-feedback-below',
    tooltipKey: 'tooltip-positive-policy-feedback',
    isDesirable: true,
    oppositeKey: 'policy_feedback_negative',
  },
  policy_feedback_negative: {
    sentenceAboveKey: 'sentence-negative-policy-feedback-above',
    sentenceBelowKey: 'sentence-negative-policy-feedback-below',
    tooltipKey: 'tooltip-negative-policy-feedback',
    isDesirable: false,
    oppositeKey: 'policy_feedback_positive',
  },
  user_strong_emotion_positive: {
    sentenceAboveKey: 'sentence-positive-emotion-above',
    sentenceBelowKey: 'sentence-positive-emotion-below',
    tooltipKey: 'tooltip-positive-emotion',
    isDesirable: true,
    oppositeKey: 'user_strong_emotion_negative',
  },
  user_strong_emotion_negative: {
    sentenceAboveKey: 'sentence-negative-emotion-above',
    sentenceBelowKey: 'sentence-negative-emotion-below',
    tooltipKey: 'tooltip-negative-emotion',
    isDesirable: false,
    oppositeKey: 'user_strong_emotion_positive',
  },
  user_effort_low: {
    sentenceAboveKey: 'sentence-low-effort-above',
    sentenceBelowKey: 'sentence-low-effort-below',
    tooltipKey: 'tooltip-low-effort',
    isDesirable: true,
    oppositeKey: 'user_effort_high',
  },
  user_effort_high: {
    sentenceAboveKey: 'sentence-high-effort-above',
    sentenceBelowKey: 'sentence-high-effort-below',
    tooltipKey: 'tooltip-high-effort',
    isDesirable: false,
    oppositeKey: 'user_effort_low',
  },
};

interface Signature {
  Args: {
    benchmarkGroup: CxBenchmarkGroup | undefined;
    appReasons: CxBenchmarkReasons | undefined;
  };
}

export default class StandingOut extends Component<Signature> {
  @service declare intl: IntlService;

  private t(key: string, options?: Record<string, string>) {
    return this.intl.t(
      `components.reporting.bespoke.fin-performance.cx-benchmark-panel.standing-out.${key}`,
      options,
    );
  }

  get isLoading(): boolean {
    return !this.args.appReasons;
  }

  get items(): StandingOutItem[] {
    let { benchmarkGroup, appReasons } = this.args;
    if (!benchmarkGroup || !appReasons) {
      return [];
    }

    let peerReasons = benchmarkGroup.reasons;

    let results: Array<StandingOutItem & { absDiff: number }> = [];
    let suppressedKeys = new Set<ReasonKey>();

    for (let [key, config] of Object.entries(REASON_CONFIG) as Array<[ReasonKey, ReasonConfig]>) {
      if (suppressedKeys.has(key)) {
        // already eliminated by a stronger opposite — skip
      } else {
        let appRate = appReasons[key];
        let peerRate = peerReasons[key];
        let diff = appRate - peerRate;
        let absDiff = Math.abs(diff);

        let oppositeKey = config.oppositeKey;
        let oppositeAbsDiff = Math.abs(appReasons[oppositeKey] - peerReasons[oppositeKey]);

        let suppressed = false;
        if (absDiff >= DIFF_THRESHOLD && oppositeAbsDiff >= DIFF_THRESHOLD) {
          if (absDiff >= oppositeAbsDiff) {
            suppressedKeys.add(oppositeKey);
          } else {
            suppressedKeys.add(key);
            suppressed = true;
          }
        }

        if (!suppressed && absDiff >= DIFF_THRESHOLD) {
          let isPositive = config.isDesirable ? diff > 0 : diff < 0;
          let sentiment: Sentiment = isPositive ? 'positive' : 'negative';

          let isAbovePeers = diff > 0;
          let sentenceKey = isAbovePeers ? config.sentenceAboveKey : config.sentenceBelowKey;

          let appPct = `${Math.round(appRate * 100)}%`;
          let peerPct = `${Math.round(peerRate * 100)}%`;

          results.push({
            sentence: this.t(sentenceKey),
            tooltip: this.t(config.tooltipKey, { appRate: appPct, peerRate: peerPct }),
            sentiment,
            dotColorClass: DOT_COLOR_MAP[sentiment],
            absDiff,
          });
        }
      }
    }

    results.sort((a, b) => b.absDiff - a.absDiff);
    return results;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::CxBenchmarkPanel::StandingOut': typeof StandingOut;
    'reporting/bespoke/fin-performance/cx-benchmark-panel/standing-out': typeof StandingOut;
  }
}
